import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	ArrowLeft,
	Upload,
	CheckCircle2,
	AlertTriangle,
	Loader2,
	X,
	Download,
} from "lucide-react";
import {
	useMappingProfile,
	useTestMapping,
	useDownloadConverted,
} from "@/hooks/useMappingProfiles";

const TestMappingPage = () => {
	const { id } = useParams<{ id: string }>();
	const [, navigate] = useLocation();
	const [file, setFile] = useState<File | null>(null);

	const { profile } = useMappingProfile(id ?? "");
	const { testMapping, isTesting, testResult } = useTestMapping();
	const { downloadConverted, isDownloading } = useDownloadConverted();

	const handleTest = async () => {
		if (!file || !id) return;
		await testMapping({ profileId: id, file });
	};

	return (
		<div className="space-y-6 max-w-5xl">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="icon"
					onClick={() => navigate(`/mapping-profiles/${id}`)}
				>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<div>
					<h1 className="text-2xl font-bold">Test Mapping</h1>
					<p className="text-muted-foreground">
						{profile?.name ?? "Loading..."} — Preview conversion
						output without importing
					</p>
				</div>
			</div>

			{/* File Upload */}
			<Card>
				<CardHeader>
					<CardTitle>Upload Test File</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-sm text-muted-foreground">
						Upload a real data file to preview how the mapping
						converts it. Only the first 5 rows per sheet will be
						shown. Nothing will be imported.
					</p>

					<div className="border-2 border-dashed rounded-lg p-6 text-center">
						{file ? (
							<div className="flex items-center justify-center gap-2">
								<CheckCircle2 className="h-5 w-5 text-green-600" />
								<span className="font-medium">{file.name}</span>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setFile(null)}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						) : (
							<label className="cursor-pointer">
								<Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
								<p className="text-sm font-medium">
									Drop .xlsx here or click to browse
								</p>
								<input
									type="file"
									className="hidden"
									accept=".xlsx,.xls"
									onChange={(e) => {
										const f = e.target.files?.[0];
										if (f) setFile(f);
									}}
								/>
							</label>
						)}
					</div>

					<Button
						onClick={handleTest}
						disabled={!file || isTesting}
						className="w-full"
					>
						{isTesting ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Testing...
							</>
						) : (
							"Run Test"
						)}
					</Button>
				</CardContent>
			</Card>

			{/* Results */}
			{testResult && (
				<>
					{/* Summary */}
					<Card>
						<CardContent className="pt-6">
							<div className="grid grid-cols-3 gap-4 text-center">
								<div>
									<div className="text-2xl font-bold">
										{testResult.totalRows}
									</div>
									<div className="text-xs text-muted-foreground">
										Total Rows
									</div>
								</div>
								<div>
									<div className="text-2xl font-bold">
										{testResult.groupedCount}
									</div>
									<div className="text-xs text-muted-foreground">
										Invoices (grouped)
									</div>
								</div>
								<div>
									<div className="text-2xl font-bold">
										{testResult.warnings.length}
									</div>
									<div className="text-xs text-muted-foreground">
										Warnings
									</div>
								</div>
							</div>
							<div className="mt-4 pt-4 border-t">
								<Button
									variant="outline"
									className="w-full"
									disabled={isDownloading || !file}
									onClick={() => {
										if (file && id)
											downloadConverted({
												profileId: id,
												file,
											});
									}}
								>
									{isDownloading ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Generating...
										</>
									) : (
										<>
											<Download className="mr-2 h-4 w-4" />
											Download Converted QuickBill Template
										</>
									)}
								</Button>
								<p className="text-xs text-muted-foreground mt-1 text-center">
									Downloads the full converted Excel in
									QuickBill import template format
								</p>
							</div>
						</CardContent>
					</Card>

					{/* Validation Summary */}
					{testResult.validation && (
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2 text-sm">
									Validation Summary
									<Badge
										variant={
											testResult.validation.failRows > 0
												? "destructive"
												: "secondary"
										}
									>
										{testResult.validation.passRows}/
										{testResult.validation.totalRows} invoices
										pass
									</Badge>
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								{/* Normalization report */}
								{testResult.validation.normalization.length > 0 && (
									<div className="space-y-1">
										<p className="text-xs font-semibold text-muted-foreground">
											Value Normalization
										</p>
										{testResult.validation.normalization.map(
											(n, i) => (
												<div
													key={i}
													className="text-xs flex items-center gap-2"
												>
													<Badge variant="outline">
														{n.sheet}.{n.column}
													</Badge>
													<span
														className={
															n.unresolved > 0
																? "text-amber-600"
																: "text-green-600"
														}
													>
														{n.resolved} resolved
														{n.unresolved > 0
															? `, ${n.unresolved} unresolved`
															: ""}
													</span>
												</div>
											),
										)}
									</div>
								)}

								{/* Issues */}
								{testResult.validation.issues.length > 0 ? (
									<div className="space-y-1 max-h-60 overflow-y-auto">
										<p className="text-xs font-semibold text-muted-foreground">
											Issues ({testResult.validation.issues.length})
										</p>
										{testResult.validation.issues.map((iss, i) => (
											<div
												key={i}
												className="flex items-start gap-2 text-xs"
											>
												<Badge
													variant={
														iss.severity === "error"
															? "destructive"
															: "outline"
													}
													className="shrink-0"
												>
													{iss.category}
												</Badge>
												<span>
													{iss.message}
													{iss.posReference && (
														<span className="text-muted-foreground">
															{" "}
															({iss.posReference})
														</span>
													)}
													{iss.suggestion && (
														<span className="text-blue-600">
															{" "}
															— {iss.suggestion}
														</span>
													)}
												</span>
											</div>
										))}
									</div>
								) : (
									<p className="text-xs text-green-600 flex items-center gap-1">
										<CheckCircle2 className="h-4 w-4" />
										No validation issues detected.
									</p>
								)}
							</CardContent>
						</Card>
					)}

					{/* Warnings */}
					{testResult.warnings.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2 text-amber-600">
									<AlertTriangle className="h-4 w-4" />
									Warnings ({testResult.warnings.length})
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-2 max-h-48 overflow-y-auto">
									{testResult.warnings.map((w, i) => (
										<div
											key={i}
											className="flex items-start gap-2 text-sm"
										>
											<Badge
												variant="outline"
												className="shrink-0"
											>
												{w.type}
											</Badge>
											<span>
												{w.message}
												{w.row && (
													<span className="text-muted-foreground">
														{" "}
														(row {w.row})
													</span>
												)}
											</span>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					)}

					{/* Preview Tables */}
					{Object.entries(testResult.preview).map(
						([sheetName, rows]) => (
							<Card key={sheetName}>
								<CardHeader>
									<CardTitle className="text-sm">
										{sheetName}{" "}
										<Badge variant="secondary" className="ml-2">
											{rows.length} rows
										</Badge>
									</CardTitle>
								</CardHeader>
								<CardContent>
									{rows.length > 0 ? (
										<div className="overflow-x-auto">
											<table className="w-full text-xs">
												<thead>
													<tr className="border-b">
														{Object.keys(
															rows[0],
														).map((col) => (
															<th
																key={col}
																className="py-1 px-2 text-left whitespace-nowrap"
															>
																{col}
															</th>
														))}
													</tr>
												</thead>
												<tbody>
													{rows.map((row, rowIdx) => (
														<tr
															key={rowIdx}
															className="border-b"
														>
															{Object.values(
																row,
															).map(
																(
																	val,
																	colIdx,
																) => (
																	<td
																		key={
																			colIdx
																		}
																		className="py-1 px-2 whitespace-nowrap max-w-[150px] truncate"
																	>
																		{val !=
																		null
																			? String(
																					val,
																				)
																			: ""}
																	</td>
																),
															)}
														</tr>
													))}
												</tbody>
											</table>
										</div>
									) : (
										<p className="text-sm text-muted-foreground">
											No data in this sheet.
										</p>
									)}
								</CardContent>
							</Card>
						),
					)}
				</>
			)}
		</div>
	);
};

export default TestMappingPage;
